import type { TFunction } from 'i18next';
import { TrendingUp, Building2, Truck, ShoppingCart } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useUpdateCart } from '~/api';
import { Select, RadioGroup } from '~/components/ui';
import type {
  Cart,
  PlatformProductLength,
  SellerProductInventory,
} from '~/graphql/graphql';
import { extractGraphQLErrorMessages } from '~/utils';
import { currencyCodeToSymbolMap } from '~/utils/data';
import type { OrderInfo } from 'src/pages/order-builder/OrderBuider';
import { useSelector, useDispatch } from 'react-redux';
import type { RootState } from '~/redux/store';
import type { Location } from '~/types';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router';
import { setSelectedLocationId } from '~/redux/reducers/cart';

interface TableHeaderProps {
  children: React.ReactNode;
  showTrendingIcon?: boolean;
  sortable?: boolean;
}

interface TableCellProps {
  type?: 'seller' | 'price' | 'stock' | 'leadTime' | 'actions' | 'simple';
  bgColor?: 'success' | 'error' | 'default';
  className?: string;
  icon?: React.ComponentType<{ className?: string }>;
  primary?: string;
  secondary?: string;
  badge?: string;
  isItalic?: boolean;
  children?: React.ReactNode;
}

interface TableRowData {
  id: string;
  seller: {
    name: string;
    hasBestPrice?: boolean;
  };
  length: string;
  price: {
    amount: string;
    unit: string;
    isWithinSetPrice: boolean;
  };
  stock: {
    amount: number;
    unit: string;
  };
  leadTime: string;
  organizationId: string;
  platformProductLengthId: string;
  minimumOrderQuantity?: number;
}

const TableHeader = ({
  children,
  showTrendingIcon = false,
  sortable = true,
}: TableHeaderProps) => (
  <th
    className={`px-6 py-3 text-left text-xs font-medium text-neut-500 uppercase tracking-wider ${sortable ? 'cursor-pointer hover:bg-neut-100' : ''} select-none`}
  >
    <div className="flex items-center gap-1">
      {children}
      {showTrendingIcon && (
        <TrendingUp className="w-3.5 h-3.5 text-prim" aria-hidden="true" />
      )}
    </div>
  </th>
);

const TableCell = ({
  children,
  bgColor = 'default',
  className = '',
}: TableCellProps) => {
  const getBgClass = () => {
    switch (bgColor) {
      case 'success':
        return 'bg-success-50';
      case 'error':
        return 'bg-error-100';
      default:
        return '';
    }
  };

  return (
    <td className={`px-6 py-4 whitespace-nowrap ${getBgClass()} ${className}`}>
      {children}
    </td>
  );
};

const getTableData = (
  sellerInventories: SellerProductInventory[] | null | undefined,
  targetPrice: number,
  t: TFunction
): TableRowData[] => {
  if (!sellerInventories) return [];

  const minPrice = Math.min(
    ...(sellerInventories?.map(
      (inv) => inv.sellerProductConfiguration.pricePerUnit
    ) ?? [])
  );
  return (
    sellerInventories?.map((inventory: SellerProductInventory) => ({
      id: inventory.id,
      seller: {
        name: inventory.organization.name,
        hasBestPrice:
          inventory.sellerProductConfiguration.pricePerUnit === minPrice,
      },
      length: inventory.platformProductsLength?.length?.displayValue,
      price: {
        amount: `${currencyCodeToSymbolMap[inventory.sellerProductConfiguration.currencyCode]}${inventory.sellerProductConfiguration.pricePerUnit}`,
        unit: '/MBF',
        isWithinSetPrice:
          inventory.sellerProductConfiguration.pricePerUnit <= targetPrice,
      },
      stock: {
        amount: inventory.quantity.value,
        unit: inventory.quantity.unit,
      },
      leadTime: inventory.sellerProductConfiguration.readyToShipDays
        ? `${inventory.sellerProductConfiguration.readyToShipDays} ${t('order_builder.days')}`
        : 'N/A',
      platformProductLengthId: inventory.platformProductsLength?.id || '',
      organizationId: inventory.organization.id,
      minimumOrderQuantity:
        inventory?.sellerProductConfiguration?.minimumOrderQuantity.value,
    })) || []
  );
};

const getTableHeaders = (t: TFunction) => [
  {
    key: 'seller',
    label: t('order_builder.inventory.seller'),
    showTrending: false,
  },
  {
    key: 'length',
    label: t('order_builder.inventory.length'),
    showTrending: false,
  },
  {
    key: 'price',
    label: t('order_builder.inventory.price'),
    showTrending: true,
  },
  {
    key: 'stock',
    label: t('order_builder.inventory.stock'),
    showTrending: false,
  },
  {
    key: 'leadTime',
    label: t('order_builder.inventory.lead_time'),
    showTrending: false,
  },
  {
    key: 'actions',
    label: t('order_builder.inventory.actions'),
    showTrending: false,
    sortable: false,
  },
];

function getAddToCartQuantity(
  remainingQuantity: number,
  availableStock: number,
  minimumOrder: number
): number {
  if (remainingQuantity <= minimumOrder) {
    return minimumOrder;
  }
  return Math.min(remainingQuantity, Math.floor(availableStock));
}

type PricesAndAvailabilityProps = {
  sellerInventories: SellerProductInventory[] | undefined | null;
  carts: Cart[];
  platformProductLengths: PlatformProductLength[] | undefined | null;
  selectedLength?: string;
  handleLengthFilterChange: (name: string, value: string) => void;
  orderInfo: OrderInfo;
};

export function PricesAndAvailability({
  sellerInventories,
  orderInfo,
  carts,
  platformProductLengths,
  selectedLength,
  handleLengthFilterChange,
}: Readonly<PricesAndAvailabilityProps>) {
  const { t } = useTranslation('order-builder');
  const { mutate: updateCart } = useUpdateCart();

  const [searchParams, setSearchParams] = useSearchParams();

  const locationId = searchParams.get('locationId');
  const returnPath = searchParams.get('returnPath');

  const user = useSelector((state: RootState) => state.auth.user);

  const locations =
    user?.companies?.flatMap((company) => company.locations ?? []) || [];

  const initialLocationId =
    returnPath === '/buyer-inventory'
      ? locationId
      : locations?.[0]?.id || undefined;

  const dispatch = useDispatch();
  const selectedLocationId = useSelector(
    (state: RootState) => state.cart.selectedLocationId
  );

  useEffect(() => {
    if (initialLocationId) {
      dispatch(setSelectedLocationId(initialLocationId));
    }
  }, [initialLocationId, dispatch]);

  // Create length filter options from platformProductLengths
  const lengthFilterOptions = [
    { value: 'all', label: t('order_builder.filters.all_lengths') },
    ...(platformProductLengths?.map((platformProductLength) => ({
      value: platformProductLength.id,
      label: platformProductLength.length?.displayValue,
    })) || []),
  ];

  /*
  // This creates a unique identifier for each cart item based on the platformProductLengthId and sellingOrganizationId, 
  // which are the two key variables when adding to cart from the order builder. 
  // This is used to determine if a cart item corresponds to a specific inventory row in the table, 
  // allowing us to disable the add to cart button for items that are already in the cart.
  */
  const cartIdentifiers = carts?.reduce<string[]>((identifiers, cart) => {
    const items = cart?.items ?? [];
    items.forEach((item) => {
      const lengthId = item?.platformProductsLengths?.id;
      const orgId = cart.sellingOrganization?.id;
      if (lengthId && orgId) {
        identifiers.push([lengthId, orgId].join('-'));
      }
    });
    return identifiers;
  }, []);

  const targetPrice = orderInfo?.targetPrice;
  const tableData = getTableData(sellerInventories, targetPrice, t);
  const tableHeaders = getTableHeaders(t);

  const { cartTotalQuantity } = useSelector((state: RootState) => state.cart);
  const targetQuantity = orderInfo?.targetOrderSize ?? 0;

  const handleAddToCart = (
    sellingOrganizationId: string,
    platformProductLengthId: string,
    stock: number,
    minimumOrder: number
  ) => {
    const remainingQuantity = targetQuantity - cartTotalQuantity;
    if (!selectedLocationId) {
      toast.error(t('order_builder.shipping_address_required'));
      return;
    }
    if (stock < minimumOrder) {
      toast.error(t('order_builder.cart_item.minimum_order_error'));
      return;
    }

    const quantity = getAddToCartQuantity(
      remainingQuantity,
      stock,
      minimumOrder
    );
    updateCart(
      {
        sellingOrganizationId,
        productId: platformProductLengthId,
        quantity: { value: quantity, unit: 'MBF' },
        locationId: selectedLocationId,
      },
      {
        onSuccess: () => {
          if (!locationId)
            setSearchParams((prev) => {
              const next = new URLSearchParams(prev);
              next.set('locationId', selectedLocationId || '');
              return next;
            });
          toast.success(t('order_builder.add_to_cart_success'));
        },
        onError: (error) =>
          toast.error(
            t('order_builder.add_to_cart_error', {
              message: extractGraphQLErrorMessages(error).join(', '),
            })
          ),
      }
    );
  };

  return (
    <div className="bg-neut-light rounded-lg border border-neut-border shadow-neut-100 ">
      <div className="px-6 py-4 border-b border-neut-border flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-neut-prim">
            {t('order_builder.comparison_title')}
          </h2>
          <div className="flex items-center gap-3">
            <label className="text-sm text-neut-sec">
              {t('order_builder.unit')}:
            </label>
            <Select
              name={'unit'}
              value={''}
              options={[{ label: 'MBF', value: 'MBF' }]}
            />
          </div>
        </div>

        <div className="p-3 border border-success-300 bg-success-50 rounded">
          <label
            htmlFor="shippingAddressSelect"
            className="flex items-center gap-2 text-sm text-neut-700 mb-3 font-medium"
          >
            {t('order_builder.shipping_address')}
            <span className="text-error-500">*</span>
          </label>
          <Select
            id="shippingAddressSelect"
            name="locationId"
            value={selectedLocationId}
            options={getAddressOptions(locations)}
            onChange={(_, value) => dispatch(setSelectedLocationId(value))}
          />
          <p className="text-xs text-neut-600 mt-1">
            {t('order_builder.shipping_address_hint')}
          </p>
        </div>
      </div>
      <div className="px-6 py-3 border-b border-neut-border bg-neut-light">
        <RadioGroup
          name="lengthFilter"
          options={lengthFilterOptions}
          value={selectedLength || 'all'}
          onChange={handleLengthFilterChange}
          label={t('order_builder.filters.filter_by_length')}
          isRow={true}
        />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-neut-50">
            <tr>
              {tableHeaders.map((header) => (
                <TableHeader
                  key={header.key}
                  showTrendingIcon={header.showTrending}
                  sortable={!!header.sortable}
                >
                  {header.label}
                </TableHeader>
              ))}
            </tr>
          </thead>
          <tbody
            data-testid="prices-and-availability-table-body"
            className="bg-neut-light divide-y divide-neut-border"
          >
            {tableData.map((row, idx) => (
              <tr key={row.id} className="hover:bg-neut-50 transition-colors">
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Building2
                      className="w-4 h-4 text-neut-400"
                      aria-hidden="true"
                    />
                    <div>
                      <p className="text-sm font-medium text-neut-900">
                        {row.seller.name}
                      </p>
                      {row.seller.hasBestPrice && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-success-100 text-success-800">
                          {t('order_builder.table.best_price')}
                        </span>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <span
                    className={`text-sm font-semibold ${row.length === 'Fixed' ? 'italic text-neut-500' : 'text-neut-900'}`}
                  >
                    {row.length}
                  </span>
                </TableCell>
                <TableCell
                  bgColor={row.price.isWithinSetPrice ? 'success' : 'error'}
                >
                  <div className="text-sm">
                    <span
                      className={`font-semibold ${row.price.isWithinSetPrice ? 'text-success-600' : 'text-error-600'}`}
                    >
                      {row.price.amount}
                    </span>
                    <span className="text-neut-sec">{row.price.unit}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="text-sm">
                    <span className="font-medium text-neut-900">
                      {row.stock.amount}
                    </span>
                    <span className="text-neut-sec ml-1">{row.stock.unit}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center text-sm">
                    <Truck
                      className="w-3.5 h-3.5 text-neut-400 mr-1"
                      aria-hidden="true"
                    />
                    <span className="text-neut-900">{row.leadTime}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() =>
                        handleAddToCart(
                          row.organizationId,
                          row.platformProductLengthId,
                          row.stock.amount,
                          row.minimumOrderQuantity ?? 1
                        )
                      }
                      title={t('order_builder.table.add_to_cart_title')}
                      className="p-2 rounded-lg transition-colors bg-prim text-neut-light cursor-pointer hover:bg-prim-hover disabled:bg-neut-200 disabled:text-neut-400 disabled:cursor-not-allowed"
                      disabled={cartIdentifiers?.includes(
                        [row.platformProductLengthId, row.organizationId].join(
                          '-'
                        )
                      )}
                      data-testid={`add-to-cart-${idx}`}
                    >
                      <ShoppingCart className="w-4 h-4" aria-hidden="true" />
                    </button>
                  </div>
                </TableCell>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function getAddressLabel(location: Location): string {
  return `${location.name} - ${location.addressLine1}, ${location.city}`;
}

function getAddressOptions(locations: Location[]) {
  return locations.map((location) => ({
    label: getAddressLabel(location),
    value: location.id,
  }));
}
